import type React from 'react';
import Icon, { type IconName } from '../atoms/Icon';

export interface Tab {
  id: string;
  label: string;
  icon?: IconName;
  disabled?: boolean;
}

export interface TabGroupProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: 'default' | 'pills';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const variantClasses = {
  default: {
    container: 'flex border-b border-border-light bg-bg-surface',
    tab: 'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
    active: 'border-action-key-default text-action-key-default',
    inactive: 'border-transparent text-text-secondary hover:text-text-primary'
  },
  pills: {
    container: 'flex space-x-1 bg-bg-surface rounded-lg p-1',
    tab: 'px-4 py-2 text-sm font-medium rounded-md transition-colors',
    active: 'bg-action-key-default text-white',
    inactive: 'text-text-secondary hover:text-text-primary hover:bg-bg-primary'
  }
};

const sizeClasses = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm', 
  lg: 'px-6 py-3 text-base'
};

export const TabGroup: React.FC<TabGroupProps> = ({
  tabs,
  activeTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  className = ''
}) => {
  const styles = variantClasses[variant];
  const sizeStyle = sizeClasses[size];

  return (
    <div className={`${styles.container} ${className}`}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        
        return (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && onTabChange(tab.id)}
            disabled={tab.disabled}
            className={`
              ${styles.tab} 
              ${sizeStyle}
              ${isActive ? styles.active : styles.inactive}
              ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              focus:outline-none focus:ring-2 focus:ring-action-key-default focus:ring-offset-2
            `}
            role="tab"
            aria-selected={isActive}
            aria-disabled={tab.disabled}
          >
            <div className="flex items-center space-x-2">
              {tab.icon && (
                <Icon 
                  name={tab.icon} 
                  size="sm" 
                  className={`transition-transform duration-200 ${isActive ? 'scale-110' : ''}`}
                />
              )}
              <span>{tab.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

TabGroup.displayName = 'TabGroup';

export default TabGroup;